import { faXmarkLarge } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Flex, Text, Tooltip } from '@pinchenterprisesnpm/friday-ui'
import {
  FlexWrapper,
  GreySpan,
} from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/days-list-item/days-list-item.style'
import { EditShiftModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/modals/edit-shift-modal/edit-shift-modal'
import { faInfoCircle, faPen } from '@fortawesome/pro-regular-svg-icons'
import { format } from 'date-fns'
import { connect, FormikProps } from 'formik'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import {
  handleDeletePress,
  handleSetAsWorkingDay,
} from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/modals/edit-shift-modal/edit-shit-modal.utils'
import { getPayRateError } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.utils'
import { DATE_FORMAT_STRING } from '@src/helpers/constants/constants'
import {
  TDay,
  TSingleDayShift,
} from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils/types'
import ErrorTooltip from '@src/ui/components/common/ErrorTooltip'
import { IBusinessEntity } from '@src/models/BusinessEntity'
import { checkIfBusinessRequiresNextWorkingDaySelection } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils'
import { NewHireStartDay } from '../new-hire-start-day/new-hire-start-day'

interface ISingleDayItemProps {
  day?: TSingleDayShift | TDay
  isEditable?: boolean
  singleDayDate?: Date
  formik?: FormikProps<IScheduleSectionFormValues>
  businessEntity?: IBusinessEntity
}

export const DaysListItem = connect<ISingleDayItemProps>(
  ({ day, formik, isEditable = true, singleDayDate, businessEntity }) => {
    const { values } = formik

    if (singleDayDate) {
      const formattedDate = format(singleDayDate, DATE_FORMAT_STRING.E_MMM_d)
      const dayOfWeek = format(singleDayDate, DATE_FORMAT_STRING.DAY_OF_WEEK)
      const time = `${values.hour_start} - ${values.hour_end}`
      const payRateError = getPayRateError(formik, Number(values.pay_value))
      const unpaidBreak = `${values.break} min`
      const payRate = `$${values.pay_value ?? ''}/hr`

      return (
        <FlexWrapper justifyContent='space-between' py='xs' pl='xs' pr='sm' mb='xs' height='60px'>
          <Box>
            <Text fontWeight='normal' fontSize='small' mt={0} mb='xs'>
              {formattedDate}
            </Text>

            <Text fontSize='small' m={0} color='secondary'>
              {dayOfWeek}
            </Text>
          </Box>

          <Flex alignItems='center'>
            <Box>
              <Text fontWeight='normal' fontSize='small' mt={0} mb='xs' align='right'>
                {time}
              </Text>

              <Flex alignItems='center' justifyContent='flex-end'>
                <Text fontSize='small' m={0} color='secondary'>
                  {formattedDate}
                </Text>
                <Text fontSize='small' m={0} mx='xs' color='secondary'>
                  •
                </Text>
                <Text fontSize='small' m={0} color='secondary'>
                  Break: <GreySpan>{unpaidBreak}</GreySpan>
                </Text>
                <Text fontSize='small' m={0} mx='xs' color='secondary'>
                  •
                </Text>
                <Text fontSize='small' m={0} color={payRateError ? 'danger' : 'secondary'}>
                  Pay Rate: <GreySpan hasError={!!payRateError}>{payRate}</GreySpan>
                </Text>

                {payRateError ? (
                  <Box ml='xs'>
                    <ErrorTooltip message={payRateError} />
                  </Box>
                ) : null}
              </Flex>
            </Box>

            {isEditable && (
              <Flex ml='20px'>
                <EditShiftModal singleDayDate={singleDayDate} />
              </Flex>
            )}
          </Flex>
        </FlexWrapper>
      )
    }

    if (!day) {
      return null
    }

    const formattedDate = format(new Date(day.date_start), DATE_FORMAT_STRING.E_MMM_d)
    const time = `${day.hour_start} - ${day.hour_end}`
    const unpaidBreak = `${day.break} min`
    const payRate = `$${day.pay_value ?? ''}/hr`
    const isDirty = 'is_dirty' in day && day.is_dirty && isEditable

    const isNonWorkingDay = 'working' in day && !day.working

    const payRateError = getPayRateError(formik, Number(day.pay_value))

    return (
      <FlexWrapper justifyContent='space-between' py='xs' pl='xs' pr='sm' mb='xs' height='60px'>
        {isNonWorkingDay ? (
          <Flex alignItems='flex-start' flexDirection='column' mt='xs' ml={27}>
            <Flex flexDirection='row' alignItems='center' ml={isDirty ? -19.5 : 0} height='14px'>
              {isDirty && (
                <Tooltip
                  text='This day will not be affected by weekday changes as it has been customized'
                  placement='left'
                >
                  <Box mr={8}>
                    <FontAwesomeIcon icon={faInfoCircle} color='#666' size='sm' />
                  </Box>
                </Tooltip>
              )}
              <Text fontWeight='normal' fontSize='small'>
                {formattedDate}
              </Text>
            </Flex>
            <Text color='danger' fontWeight='normal' fontSize='small' mt={10}>
              Non-working day
            </Text>
          </Flex>
        ) : (
          <Box mt={-13} ml='xs'>
            <Flex
              flexDirection='row'
              alignItems='center'
              ml={isDirty ? 3 : 22}
              mt='md'
              mb='xs'
              height='14px'
            >
              {isDirty && (
                <Tooltip
                  text='This day will not be affected by weekday changes as it has been customized'
                  placement='left'
                >
                  <Box mr={9}>
                    <FontAwesomeIcon icon={faInfoCircle} color='#666' size='sm' />
                  </Box>
                </Tooltip>
              )}
              <Text fontWeight='normal' fontSize='small'>
                {formattedDate}
              </Text>
            </Flex>

            {businessEntity &&
              checkIfBusinessRequiresNextWorkingDaySelection(businessEntity) &&
              'working' in day && <NewHireStartDay day={day} isEditable={isEditable} />}
          </Box>
        )}

        {isNonWorkingDay ? (
          <Flex alignItems='center' mr='xs'>
            <Button size='small' onClick={() => handleSetAsWorkingDay(day, formik)}>
              <FontAwesomeIcon icon={faPen} />
            </Button>
          </Flex>
        ) : (
          <Flex mr='xs' alignItems='center'>
            <Box>
              <Text fontWeight='normal' fontSize='small' mt={0} mb='xs' align='right'>
                {time}
              </Text>

              <Flex alignItems='center' justifyContent='flex-end'>
                <Text fontSize='small' m={0} color='secondary'>
                  Break: <GreySpan>{unpaidBreak}</GreySpan>
                </Text>
                <Text fontSize='small' m={0} mx='xs' color='secondary'>
                  •
                </Text>
                <Text fontSize='small' m={0} color={payRateError ? 'danger' : 'secondary'}>
                  Pay Rate: <GreySpan hasError={!!payRateError}>{payRate}</GreySpan>
                </Text>

                {payRateError ? (
                  <Box ml='xs'>
                    <ErrorTooltip message={payRateError} />
                  </Box>
                ) : null}
              </Flex>
            </Box>

            {isEditable && (
              <Flex ml='20px'>
                <Button
                  type='danger'
                  size='small'
                  mr='xs'
                  onClick={() => handleDeletePress(day, formik)}
                >
                  <FontAwesomeIcon icon={faXmarkLarge} />
                </Button>

                <EditShiftModal buttonSize='small' day={day} />
              </Flex>
            )}
          </Flex>
        )}
      </FlexWrapper>
    )
  }
)
